import { getMockCatalog } from '@/modules/search/utils/mock-catalog'
import type { NormalizedProduct } from '@/modules/search/types'
import { comparisonEngine } from '../engines'
import {
  comparisonConfigRepository,
  comparisonFavoritesRepository,
  comparisonHistoryRepository,
} from '../repositories'
import type {
  ComparisonFilters,
  ComparisonHistoryEntry,
  ComparisonProductInput,
  ComparisonSortBy,
  ComparisonStatistics,
  ComparisonSummary,
  ProductComparisonGroup,
  RankedOffer,
  RankingWeights,
} from '../types'

/**
 * ComparisonService — orchestrates search → compare → history/stats.
 */
export class ComparisonService {
  searchAndCompare(
    term: string,
    filters: ComparisonFilters = {},
    sortBy: ComparisonSortBy = 'score_desc',
    options: { persistHistory?: boolean } = {},
  ) {
    const weights = comparisonConfigRepository.getWeights()
    comparisonEngine.setWeights(weights)

    const catalog = getMockCatalog()
    const matched = catalog.filter((p) => matches(p, term))
    const inputs = matched.map(toInput)
    const result = comparisonEngine.compare(inputs, filters, sortBy)

    if (options.persistHistory !== false && term.trim()) {
      for (const group of result.groups.slice(0, 5)) {
        if (!group.recommended) continue
        comparisonHistoryRepository.add({
          product: group.label,
          supplierWinner: group.recommended.supplier,
          economy: group.economy,
        })
      }
    }

    return result
  }

  compareInputs(
    products: ComparisonProductInput[],
    filters: ComparisonFilters = {},
    sortBy: ComparisonSortBy = 'score_desc',
  ) {
    comparisonEngine.setWeights(comparisonConfigRepository.getWeights())
    return comparisonEngine.compare(products, filters, sortBy)
  }

  getWeights(): RankingWeights {
    return comparisonConfigRepository.getWeights()
  }

  saveWeights(weights: RankingWeights): RankingWeights {
    const normalized = comparisonEngine.setWeights(weights)
    return comparisonConfigRepository.saveWeights(normalized)
  }

  listHistory(): ComparisonHistoryEntry[] {
    return comparisonHistoryRepository.list()
  }

  toggleFavorite(supplier: string): string[] {
    return comparisonFavoritesRepository.toggle(supplier)
  }

  listFavorites(): string[] {
    return comparisonFavoritesRepository.list()
  }

  statistics(): ComparisonStatistics {
    const history = this.listHistory()
    const wins = new Map<string, { wins: number; economy: number; scores: number[] }>()

    for (const entry of history) {
      const current = wins.get(entry.supplierWinner) ?? { wins: 0, economy: 0, scores: [] }
      current.wins += 1
      current.economy += entry.economy
      wins.set(entry.supplierWinner, current)
    }

    // Enrich with live catalog signals
    const catalog = getMockCatalog()
    const bySupplier = new Map<string, NormalizedProduct[]>()
    for (const product of catalog) {
      const list = bySupplier.get(product.supplier) ?? []
      list.push(product)
      bySupplier.set(product.supplier, list)
    }

    let cheapestSupplier: string | null = null
    let cheapestAvg = Number.POSITIVE_INFINITY
    let fastestSupplier: string | null = null
    let fastestAvg = Number.POSITIVE_INFINITY

    for (const [supplier, products] of bySupplier) {
      const avgPrice = products.reduce((s, p) => s + p.pixPrice, 0) / products.length
      const avgDelivery =
        products.reduce((s, p) => s + p.estimatedDelivery, 0) / products.length
      if (avgPrice < cheapestAvg) {
        cheapestAvg = avgPrice
        cheapestSupplier = supplier
      }
      if (avgDelivery < fastestAvg) {
        fastestAvg = avgDelivery
        fastestSupplier = supplier
      }
    }

    const mostUsed =
      [...wins.entries()].sort((a, b) => b[1].wins - a[1].wins)[0]?.[0] ?? cheapestSupplier

    const topSuppliers = [...wins.entries()]
      .map(([supplier, data]) => ({
        supplier,
        wins: data.wins,
        economy: round2(data.economy),
        avgScore: 0,
      }))
      .sort((a, b) => b.wins - a.wins || b.economy - a.economy)
      .slice(0, 10)

    // Fill top 10 with catalog suppliers if history is thin
    if (topSuppliers.length < 10) {
      for (const supplier of bySupplier.keys()) {
        if (topSuppliers.some((t) => t.supplier === supplier)) continue
        topSuppliers.push({ supplier, wins: 0, economy: 0, avgScore: 70 })
        if (topSuppliers.length >= 10) break
      }
    }

    const priceChart = [...bySupplier.entries()]
      .map(([supplier, products]) => ({
        supplier,
        value: round2(products.reduce((s, p) => s + p.pixPrice, 0) / products.length),
      }))
      .sort((a, b) => a.value - b.value)
      .slice(0, 10)

    const scoreChart = topSuppliers.map((t) => ({
      supplier: t.supplier,
      value: t.avgScore || 70 + t.wins,
    }))

    const economyChart = topSuppliers.map((t) => ({
      supplier: t.supplier,
      value: t.economy,
    }))

    return {
      cheapestSupplier,
      fastestSupplier,
      mostUsedSupplier: mostUsed,
      totalEconomy: round2(history.reduce((s, h) => s + h.economy, 0)),
      topSuppliers,
      charts: {
        price: priceChart,
        score: scoreChart,
        economy: economyChart,
      },
    }
  }
}

function matches(product: NormalizedProduct, term: string): boolean {
  if (!term.trim()) return true
  const q = term.toLowerCase()
  return `${product.description} ${product.brand} ${product.model} ${product.sku}`
    .toLowerCase()
    .includes(q)
}

function toInput(product: NormalizedProduct): ComparisonProductInput {
  return {
    id: product.id,
    supplier: product.supplier,
    supplierName: product.supplier,
    description: product.description,
    brand: product.brand,
    model: product.model,
    sku: product.sku,
    ean: product.ean,
    imageUrl: product.imageUrl,
    category: guessCategory(product.description),
    pixPrice: product.pixPrice,
    cardPrice: product.cardPrice,
    purchasePrice: product.purchasePrice,
    installments: product.installments,
    freight: Math.round(product.pixPrice * 0.02 * 100) / 100,
    estimatedDelivery: product.estimatedDelivery,
    stock: product.stock,
    reliability: reliabilityFor(product.supplier),
    lastUpdate: product.lastUpdate,
    warranty: '12 meses',
    productUrl: product.productUrl,
  }
}

function guessCategory(description: string): string {
  const d = description.toLowerCase()
  if (d.includes('notebook')) return 'Notebook'
  if (d.includes('ssd') || d.includes('nvme')) return 'SSD'
  if (d.includes('monitor')) return 'Monitor'
  if (d.includes('placa')) return 'Placa de Vídeo'
  return 'Geral'
}

function reliabilityFor(supplier: string): number {
  const map: Record<string, number> = {
    pauta: 90,
    oderco: 88,
    agis: 85,
    allnations: 86,
    ingram: 92,
    snd: 84,
    kabum: 80,
    terabyte: 78,
    pichau: 79,
    amazon: 88,
    mercadolivre: 75,
    magazineluiza: 82,
  }
  return map[supplier] ?? 70
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const comparisonService = new ComparisonService()

export type {
  ComparisonFilters,
  ComparisonSummary,
  ProductComparisonGroup,
  RankedOffer,
}
